import { addToOutbox } from '@/storage/outbox';
import { isUuid } from '@/storage/repair-categories';
import { createOutboxEntry } from './outbox-writer';
export async function writeOutboxForChanges(prev, current) {
    const entries = [];
    const prevMap = new Map(prev.map((b) => [b.id, b]));
    const currMap = new Map(current.map((b) => [b.id, b]));
    // Detect new and modified budgets
    for (const [id, curr] of currMap) {
        if (curr.storageMode !== 'CloudBound')
            continue;
        const old = prevMap.get(id);
        if (!old) {
            // New CloudBound budget
            collectBudgetCreate(curr, entries);
            continue;
        }
        // Budget-level changes (compare by reference — reducer creates new objects on change)
        if (old !== curr) {
            collectDiffBudget(old, curr, entries);
        }
    }
    // Detect deleted budgets
    for (const [id, old] of prevMap) {
        if (old.storageMode !== 'CloudBound')
            continue;
        if (!currMap.has(id)) {
            entries.push(createOutboxEntry('budget', id, id, 'delete', null, old.version ?? 1));
        }
    }
    for (const entry of entries) {
        await addToOutbox(entry);
    }
    return entries.length;
}
function collectBudgetCreate(budget, out) {
    out.push(createOutboxEntry('budget', budget.id, budget.id, 'create', budgetPayload(budget), 0));
    for (const cat of budget.categories) {
        // Guard: сущности с невалидным (не-GUID) id сервер отклонит (MalformedOperation).
        // Не эмитим по ним операции — миграция dbm0.0.6 чинит такие id, это защита на будущее.
        if (!isUuid(cat.id))
            continue;
        out.push(createOutboxEntry('category', cat.id, budget.id, 'create', categoryPayload(cat), 0));
    }
    for (const tx of budget.transactions) {
        if (!isUuid(tx.id))
            continue;
        out.push(createOutboxEntry('transaction', tx.id, budget.id, 'create', transactionPayload(tx), 0));
    }
}
function collectDiffBudget(old, curr, out) {
    // Budget fields changed?
    if (budgetFieldsChanged(old, curr)) {
        out.push(createOutboxEntry('budget', curr.id, curr.id, 'update', budgetPayload(curr), old.version ?? 1));
    }
    // Diff categories
    collectDiffEntities(old.categories, curr.categories, curr.id, 'category', categoryPayload, out);
    // Diff transactions
    collectDiffEntities(old.transactions, curr.transactions, curr.id, 'transaction', transactionPayload, out);
}
function budgetFieldsChanged(a, b) {
    return (a.name !== b.name ||
        a.currency !== b.currency ||
        a.icon !== b.icon ||
        a.weekStart !== b.weekStart ||
        a.savingsEnabled !== b.savingsEnabled ||
        a.showCurrentBalance !== b.showCurrentBalance ||
        a.isArchived !== b.isArchived);
}
function collectDiffEntities(oldArr, newArr, budgetId, entityType, toPayload, out) {
    const oldMap = new Map(oldArr.map((e) => [e.id, e]));
    const newMap = new Map(newArr.map((e) => [e.id, e]));
    // New and modified
    for (const [id, curr] of newMap) {
        // Guard: не эмитим операции по сущностям с невалидным (не-GUID) id — сервер их отклонит.
        if (!isUuid(id))
            continue;
        const old = oldMap.get(id);
        if (!old) {
            out.push(createOutboxEntry(entityType, id, budgetId, 'create', toPayload(curr), 0));
        }
        else if (old !== curr) {
            // Reference changed — entity was modified
            out.push(createOutboxEntry(entityType, id, budgetId, 'update', toPayload(curr), old.version ?? 1));
        }
    }
    // Deleted
    for (const [id, old] of oldMap) {
        if (!isUuid(id))
            continue;
        if (!newMap.has(id)) {
            out.push(createOutboxEntry(entityType, id, budgetId, 'delete', null, old.version ?? 1));
        }
    }
}
function budgetPayload(b) {
    return {
        budgetId: b.id,
        name: b.name,
        currency: b.currency,
        icon: b.icon,
        weekStart: b.weekStart,
        savingsEnabled: b.savingsEnabled ?? false,
        showCurrentBalance: b.showCurrentBalance ?? true,
        status: b.isArchived ? 'Archived' : 'Active',
        storageMode: b.storageMode ?? 'LocalOnly',
        version: b.version ?? 1,
    };
}
function capitalize(s) {
    return s.charAt(0).toUpperCase() + s.slice(1);
}
function categoryPayload(c) {
    return {
        categoryId: c.id,
        label: c.label,
        icon: c.icon,
        type: capitalize(c.type),
        version: c.version ?? 1,
    };
}
function transactionPayload(t) {
    return {
        transactionId: t.id,
        date: t.date,
        plannedAmount: t.planned,
        actualAmount: t.actual,
        type: capitalize(t.type),
        description: t.description,
        categoryId: t.category ?? null,
        fromSavings: t.fromSavings ?? false,
        state: t.actual != null ? 'ActualOnly' : 'Planned',
        seriesId: t.seriesId ?? null,
        isStart: t.isStart ?? false,
        version: t.version ?? 1,
    };
}
